"use client";
import { useContext, useEffect, useState } from "react";
import { CartContext } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { trimText } from "@/lib/trimText";
import { ShoppingCart } from "lucide-react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import RemoveFromCart from "@/app/cart/components/RemoveFromCart";
import Link from "next/link";
import { formatCurrency } from "@/utils/formatCurrency";
import UpdateCart from "@/app/cart/components/UpdateCart";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const AddToCart = ({ product }) => {
  const { addToCart, cart, getTotalPrice } = useContext(CartContext);
  const [cartProducts, setCartProducts] = useState([]);
  const [cartTotalPrice, setCartTotalPrice] = useState(0);

  useEffect(() => {
    setCartProducts(cart);
    setCartTotalPrice(getTotalPrice());
  }, [cart]);

  const handleAddToCart = () => {
    if (product?.countInStock === 0) {
      toast({
        title: "Out of Stock",
        description: "This product is currently out of stock. Please contact us for more information.",
      });
    } else {
      addToCart(product);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });

      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          currency: "BDT",
          value: product?.discountedPrice,
          items: [
            {
              item_id: product?._id,
              item_name: product?.title,
              item_category: product?.category,
              item_subcategory: product?.subCategory,
              item_type: product?.type || product?.brand,
              item_variant: product?.variant || "",
              price: product?.discountedPrice,
              quantity: 1,
            },
          ],
        },
      });
    }
  };

  return (
    <Sheet>
      <SheetTrigger>
        <Button
          className="flex items-center justify-center bg-custom-blue text-white w-full gap-2"
          onClick={handleAddToCart}
        >
          <ShoppingCart size={16} /> Add to Cart
        </Button>
      </SheetTrigger>
      <SheetContent>
        {cartProducts?.length > 0 ? (
          <>
            <SheetHeader>
              <SheetTitle>Shopping Cart</SheetTitle>
              <SheetDescription>
                <div className="flex items-center bg-custom-blue shadow-sm rounded-full px-4 py-2">
                  <span className="bg-custom-yellow text-gray-900 text-base font-semibold rounded-full px-3 py-1">
                    Total
                  </span>
                  <h3 className="text-white text-lg font-semibold uppercase mx-4">
                    {formatCurrency(cartTotalPrice)}
                  </h3>
                </div>
              </SheetDescription>
            </SheetHeader>
            <table className="min-w-full mt-2 bg-white divide-y divide-gray-200">
              <tbody>
                {cartProducts?.map((data, index) => (
                  <tr key={index}>
                    <td className="text-sm py-4">
                      <div className="flex items-start gap-4">
                        <Link
                          href={`/${data?.slug}`}
                          className="w-16 h-16 shrink-0"
                        >
                          <Avatar className="h-16 w-16">
                            <AvatarImage
                              src={data?.images?.[0]}
                              alt={data?.title}
                            />
                            <AvatarFallback>{data?.title}</AvatarFallback>
                          </Avatar>
                        </Link>
                        <div className="flex flex-col gap-2 w-full">
                          <div className="flex justify-between items-center w-full">
                            <p className="text-sm font-medium">
                              {trimText(data?.title, 20)}
                            </p>
                            <Badge variant="outline">
                              {formatCurrency(
                                Math.round(
                                  data?.discountedPrice * data?.quantity
                                )
                              )}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-500">
                            {data?.author?.name ? (
                              <>
                                <span>By: </span>
                                <span className="text-sky-600">
                                  {data?.author?.name}
                                </span>
                              </>
                            ) : (
                              <>
                                <span>Brand: </span>
                                <span className="text-sky-600">
                                  {data?.brand}
                                </span>
                              </>
                            )}
                          </div>
                          <div className="flex gap-4 items-center">
                            <RemoveFromCart data={data} />
                            <UpdateCart data={data} />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <SheetFooter className="w-full flex flex-col items-center mt-4">
              <div className="w-full flex items-center gap-4">
                <Link href="/checkout" className="w-full">
                  <Button className="w-full" variant="outline">
                    Checkout
                  </Button>
                </Link>
                <Link href="/cart" className="w-full">
                  <Button className="w-full">View Cart</Button>
                </Link>
              </div>
            </SheetFooter>
            <SheetClose className="text-center w-full mt-4 underline">
              Continue Shopping
            </SheetClose>
          </>
        ) : (
          <Card className="flex items-center justify-center flex-col gap-4 mt-10">
            <CardHeader>
              <CardTitle className="text-custom-blue text-center">
                Your cart is empty.
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 items-center">
              <p className="text-center text-sm max-w-xl">
                Your Shopping Cart lives to serve. Give it purpose — fill it with books, stationaries, and more.
              </p>
              <Link href="/">
                <Button>Continue Shopping</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default AddToCart;
